/**
 * Aurora domain types.
 *
 * This is the contract the entire system depends on — backend, web, mobile, AI.
 * Everything else is derived from these shapes.
 *
 * Conventions:
 *  - Ids are branded strings so a SkillId can never be passed where a ChildId belongs.
 *  - Timestamps are epoch milliseconds (number), never Date objects — they cross
 *    process/serialization boundaries constantly.
 *  - `age` is NEVER stored. It is derived from birthDate on read.
 */

// ─────────────────────────────────────────────────────────────
// Branded ids
// ─────────────────────────────────────────────────────────────

declare const brand: unique symbol
type Brand<T, B> = T & { readonly [brand]: B }

export type HouseholdId = Brand<string, 'Household'>
export type ChildId = Brand<string, 'Child'>
export type FrameworkId = Brand<string, 'Framework'>
export type DomainId = Brand<string, 'Domain'>
export type SkillId = Brand<string, 'Skill'>
export type SkillStateId = Brand<string, 'SkillState'>
export type ObservationId = Brand<string, 'Observation'>
export type CycleId = Brand<string, 'Cycle'>
export type WeekId = Brand<string, 'Week'>
export type DayId = Brand<string, 'Day'>
export type BlockId = Brand<string, 'Block'>
export type ArtifactId = Brand<string, 'Artifact'>
export type AdaptationId = Brand<string, 'Adaptation'>
export type InsightId = Brand<string, 'Insight'>
export type InterestId = Brand<string, 'Interest'>
export type SnapshotId = Brand<string, 'RubricSnapshot'>
export type ConversationId = Brand<string, 'Conversation'>
export type MessageId = Brand<string, 'Message'>

/** Epoch milliseconds. */
export type Timestamp = number
/** ISO date, no time component: "2026-06-01". Used for calendar days. */
export type CalendarDate = string

// ─────────────────────────────────────────────────────────────
// Shared enums
// ─────────────────────────────────────────────────────────────

/** 1..5. See LevelDescriptor for what each means per skill. */
export type Level = 1 | 2 | 3 | 4 | 5

export const LEVEL_LABELS = {
  1: 'Emerging',
  2: 'Developing',
  3: 'Proficient',
  4: 'Advanced',
  5: 'Exceptional',
} as const satisfies Record<Level, string>

/** Approximate age-equivalence for each level, in months. Guidance only — never shown as a deficit. */
export const LEVEL_AGE_BANDS = {
  1: [18, 24],
  2: [24, 30],
  3: [30, 36],
  4: [36, 48],
  5: [48, 60],
} as const satisfies Record<Level, readonly [number, number]>

export type DomainKey =
  | 'literacy'
  | 'language'
  | 'logic'
  | 'math'
  | 'fineMotor'
  | 'grossMotor'
  | 'socialEmotional'
  | 'executiveFunction'
  | 'creative'
  | 'time'
  | 'science'
  | 'secondLanguage'
  | 'selfCare'

export const DOMAIN_KEYS = [
  'literacy', 'language', 'logic', 'math', 'fineMotor', 'grossMotor',
  'socialEmotional', 'executiveFunction', 'creative', 'time', 'science',
  'secondLanguage', 'selfCare',
] as const satisfies readonly DomainKey[]

/** How a skill showed up in one observation. */
export type EvidenceSignal = 'demonstrated' | 'partial' | 'struggled' | 'refused'

/** Emotional read on an activity. Drives the frustration + interest engines. */
export type Affect = 'delighted' | 'engaged' | 'neutral' | 'frustrated' | 'refused'

/** Direction of travel for a skill. `stalled` is what triggers adaptation. */
export type Trend = 'rising' | 'steady' | 'stalled' | 'regressing'

// ─────────────────────────────────────────────────────────────
// Household & Child
// ─────────────────────────────────────────────────────────────

export interface Household {
  _id: HouseholdId
  name: string
  createdAt: Timestamp
  updatedAt: Timestamp
}

/**
 * Pronouns are explicit and required at creation with a neutral default.
 * Never inferred from a name.
 */
export interface Pronouns {
  subject: string // she / he / they
  object: string  // her / him / them
  possessive: string // her / his / their
}

export const DEFAULT_PRONOUNS: Pronouns = {
  subject: 'they',
  object: 'them',
  possessive: 'their',
}

export interface Child {
  _id: ChildId
  householdId: HouseholdId
  name: string
  /** ISO date. Age is always derived from this — never stored. */
  birthDate: CalendarDate
  pronouns: Pronouns
  /** Her own drawing, used as her avatar. */
  avatarArtifactId?: ArtifactId
  activeFrameworkId: FrameworkId
  activeCycleId?: CycleId
  createdAt: Timestamp
  updatedAt: Timestamp
  deleted?: boolean
}

// ─────────────────────────────────────────────────────────────
// Framework → Domain → Skill
// ─────────────────────────────────────────────────────────────

export interface Framework {
  _id: FrameworkId
  name: string
  version: string
  source?: string
  isBuiltIn: boolean
  createdAt: Timestamp
  updatedAt: Timestamp
}

export interface DomainDef {
  _id: DomainId
  frameworkId: FrameworkId
  key: DomainKey
  name: string
  description: string
  /** Token name in the design system, e.g. "domain.logic" — never a raw hex. */
  colorToken: string
  iconKey: string
  sortOrder: number
}

export interface LevelDescriptor {
  level: Level
  /** Concrete, observable behavior. This is what makes assessment reproducible. */
  descriptor: string
  /** Real examples, ideally from actual observation. */
  exemplars: string[]
}

export interface Skill {
  _id: SkillId
  domainId: DomainId
  domainKey: DomainKey
  key: string
  name: string
  description: string
  /** Exactly 5, one per level. */
  levelDescriptors: LevelDescriptor[]
  /** Typical age band in months for reaching level 3. Guidance only. */
  typicalMonths?: readonly [number, number]
  /** The skill graph. Enables "what's actually blocking this?" reasoning. */
  prerequisites: SkillId[]
  /** How a parent can probe this skill in the moment. */
  assessmentHints: string[]
  sortOrder: number
}

// ─────────────────────────────────────────────────────────────
// SkillState — the living rubric
// ─────────────────────────────────────────────────────────────

export interface ParentOverride {
  level: Level
  note?: string
  at: Timestamp
}

export interface SkillState {
  _id: SkillStateId
  childId: ChildId
  skillId: SkillId
  domainKey: DomainKey
  level: Level
  /** 0..1 — how much we trust this level given evidence recency, count, consistency. */
  confidence: number
  trend: Trend
  /** The evidence trail. Tap a level, read these. */
  evidenceIds: ObservationId[]
  observationCount: number
  lastMovedAt?: Timestamp
  lastObservedAt?: Timestamp
  /** Set when trend becomes 'stalled'. Drives the adaptation engine. */
  stallSince?: Timestamp
  /** The parent is ground truth. This always wins and stays visible. */
  parentOverride?: ParentOverride
  notes?: string
  updatedAt: Timestamp
  deleted?: boolean
}

export interface RubricSnapshot {
  _id: SnapshotId
  childId: ChildId
  takenAt: Timestamp
  reason: 'cycle_start' | 'cycle_end' | 'manual' | 'scheduled'
  cycleId?: CycleId
  states: Array<{
    skillId: SkillId
    domainKey: DomainKey
    level: Level
    confidence: number
    trend: Trend
  }>
  /** Generated prose summary of where the child is. */
  narrative?: string
  updatedAt: Timestamp
}

// ─────────────────────────────────────────────────────────────
// Observation — the atomic unit of evidence
// ─────────────────────────────────────────────────────────────

export type ObservationSource =
  | 'voice'
  | 'text'
  | 'quick_rating'
  | 'artifact'
  | 'kid_mode'
  | 'agent_inferred'

/** -1 regressed · 0 steady · 1 progress · 2 milestone */
export type QuickRating = -1 | 0 | 1 | 2

/** Immutable. Extraction can be re-run against this; this never changes. */
export interface ObservationRaw {
  transcript?: string
  audioArtifactId?: ArtifactId
  text?: string
  rating?: QuickRating
}

export interface SkillEvidence {
  skillId: SkillId
  signal: EvidenceSignal
  /** What the extractor thinks the level should be, if it has a view. */
  suggestedLevel?: Level
  /** Supporting fragment from the transcript. */
  quote?: string
}

export interface InterestSignal {
  topic: string
  strength: number // 0..1
}

export interface ObservationExtraction {
  summary: string
  domainKeys: DomainKey[]
  skillEvidence: SkillEvidence[]
  /** Verbatim only. Never paraphrased — these become the Growth Story. */
  childQuotes: string[]
  interestSignals: InterestSignal[]
  affect?: Affect
  durationMinutes?: number
}

export interface Observation {
  _id: ObservationId
  childId: ChildId
  /** When it happened (may be backdated). */
  occurredAt: Timestamp
  /** When it was recorded. */
  capturedAt: Timestamp
  source: ObservationSource
  raw: ObservationRaw
  blockId?: BlockId
  artifactIds?: ArtifactId[]
  extracted?: ObservationExtraction
  extractionModel?: string
  extractionConfidence?: number
  /** Marks human-corrected extractions as gold. */
  parentEdited: boolean
  /** Client-generated id for optimistic create; platform rekeys on sync. */
  client_id?: string
  updatedAt: Timestamp
  deleted?: boolean
}

// ─────────────────────────────────────────────────────────────
// Plan: Cycle → Week → Day → Block
// ─────────────────────────────────────────────────────────────

export interface CycleGoal {
  domainKey: DomainKey
  statement: string
  targetSkillIds: SkillId[]
}

export interface Cycle {
  _id: CycleId
  childId: ChildId
  number: number
  title: string
  startDate: CalendarDate
  endDate: CalendarDate
  goals: CycleGoal[]
  /** Ordered. The first entries are what this cycle is really about. */
  priorities: DomainKey[]
  /** Carried-forward insights, rendered as constraints for the planner. */
  principles: string[]
  status: 'draft' | 'active' | 'completed' | 'abandoned'
  sourceRubricSnapshotId?: SnapshotId
  createdAt: Timestamp
  updatedAt: Timestamp
  deleted?: boolean
}

export interface Week {
  _id: WeekId
  cycleId: CycleId
  childId: ChildId
  number: number
  theme: string
  focusByDomain: Partial<Record<DomainKey, string>>
  /** Gather on Sunday. */
  materialsList: string[]
  startDate: CalendarDate
  updatedAt: Timestamp
  deleted?: boolean
}

export interface Day {
  _id: DayId
  weekId: WeekId
  childId: ChildId
  date: CalendarDate
  note?: string
  updatedAt: Timestamp
  deleted?: boolean
}

export type BlockStatus = 'pending' | 'done' | 'partial' | 'skipped'

export interface Block {
  _id: BlockId
  dayId: DayId
  childId: ChildId
  slot: number
  /** Honest estimate. Cycle 1 proved ~10 min is the truth, not 20. */
  estimatedMinutes: number
  domainKey: DomainKey
  skillIds: SkillId[]
  title: string
  instructions: string
  /** One line. Always present. Builds parent understanding and trust. */
  why: string
  artifactIds: ArtifactId[]
  status: BlockStatus
  completedAt?: Timestamp
  observationIds: ObservationId[]
  /** Provenance when the adaptation engine changed this block. */
  adaptedFrom?: BlockId
  updatedAt: Timestamp
  deleted?: boolean
}

/** Woven into normal life, not scheduled as blocks. */
export interface Ritual {
  key: string
  title: string
  description: string
  estimatedMinutes: number
}

// ─────────────────────────────────────────────────────────────
// Artifacts
// ─────────────────────────────────────────────────────────────

export type ArtifactKind =
  | 'photo'
  | 'drawing_scan'
  | 'audio'
  | 'video'
  | 'generated_interactive'
  | 'generated_worksheet'
  | 'generated_deck'
  | 'book'
  | 'report'

export interface DevelopmentalMarker {
  /** e.g. "Lowenfeld", "Gentry" — named frameworks make the reading reproducible. */
  framework: string
  stage: string
  evidence: string
}

export interface ArtifactAnalysis {
  describedContent: string
  developmentalMarkers: DevelopmentalMarker[]
  skillEvidence: SkillEvidence[]
  comparisonToPrevious?: {
    artifactId: ArtifactId
    changes: string[]
  }
  /** What to say TO the child. Specific praise about a choice she made. */
  celebrationNote: string
}

export interface Artifact {
  _id: ArtifactId
  childId: ChildId
  kind: ArtifactKind
  title: string
  createdAt: Timestamp
  storage: {
    blobId?: string
    url?: string
    /** For generated parent-facing material only. Never executed in Kid Mode. */
    html?: string
    mimeType?: string
    bytes?: number
    width?: number
    height?: number
  }
  source: 'child' | 'parent' | 'agent'
  blockId?: BlockId
  observationIds: ObservationId[]
  analysis?: ArtifactAnalysis
  generation?: {
    prompt: string
    model: string
    spec?: InteractiveSpec
    version: number
  }
  tags: string[]
  client_id?: string
  updatedAt: Timestamp
  deleted?: boolean
}

// ─────────────────────────────────────────────────────────────
// Interactive specs
// ─────────────────────────────────────────────────────────────

export type InteractiveKind =
  | 'tangram'
  | 'matrix_reasoning'
  | 'sight_word_deck'
  | 'counting_game'
  | 'pattern_builder'
  | 'deduction_puzzle'
  | 'sorting_game'
  | 'sequence_order'
  | 'letter_tracing'
  | 'reading_timer'
  | 'memory_pairs'
  | 'model_3d'
  | 'story_builder'

export const INTERACTIVE_KINDS = [
  'tangram', 'matrix_reasoning', 'sight_word_deck', 'counting_game',
  'pattern_builder', 'deduction_puzzle', 'sorting_game', 'sequence_order',
  'letter_tracing', 'reading_timer', 'memory_pairs', 'model_3d', 'story_builder',
] as const satisfies readonly InteractiveKind[]

export interface InteractiveSpecBase {
  kind: InteractiveKind
  title: string
  difficulty: Level
  /** From active Interests. Content-gating her attention is the whole game. */
  theme?: string
  targetSkillIds: SkillId[]
  successCriteria: {
    autoLogObservation: boolean
    /** e.g. { minCorrect: 4, ofTotal: 5 } */
    thresholds?: Record<string, number>
  }
}

/**
 * Per-kind config is validated by zod schemas in ../interactives.
 * Kept as unknown here so this file stays dependency-free.
 */
export interface InteractiveSpec extends InteractiveSpecBase {
  config: Record<string, unknown>
}

export interface InteractiveResult {
  specKind: InteractiveKind
  startedAt: Timestamp
  completedAt: Timestamp
  durationMs: number
  completed: boolean
  score?: { correct: number; total: number }
  /** Per-item detail for fine-grained evidence. */
  items?: Array<{ id: string; correct: boolean; attempts: number; ms: number }>
  /** Anything kind-specific worth keeping. */
  detail?: Record<string, unknown>
  /** What a material saved to pick up from next time. The host keeps it on the material. */
  carry?: unknown
  /** Every attempt at an item during the play (a letter, a word, a card): her memory is built from these. */
  events?: import('../memory').ItemEvent[]
}

// ─────────────────────────────────────────────────────────────
// Interests & Insights
// ─────────────────────────────────────────────────────────────

export interface Interest {
  _id: InterestId
  childId: ChildId
  topic: string
  /** 0..1, decays over time. */
  strength: number
  firstSeenAt: Timestamp
  lastSeenAt: Timestamp
  sourceObservationIds: ObservationId[]
  status: 'active' | 'fading' | 'archived'
  updatedAt: Timestamp
  deleted?: boolean
}

/**
 * Durable learned principles. Injected into every planning prompt as
 * hard constraints so the system structurally cannot repeat a known mistake.
 */
export interface Insight {
  _id: InsightId
  childId: ChildId
  statement: string
  detail: string
  /** What to DO about it. */
  implication: string
  confidence: number
  sourceObservationIds: ObservationId[]
  discoveredAt: Timestamp
  status: 'active' | 'superseded'
  supersededBy?: InsightId
  appliedInCycleIds: CycleId[]
  updatedAt: Timestamp
  deleted?: boolean
}

// ─────────────────────────────────────────────────────────────
// Adaptation
// ─────────────────────────────────────────────────────────────

export type AdaptationTrigger =
  | 'stall'
  | 'mastery'
  | 'frustration'
  | 'interest'
  | 'prerequisite_block'
  | 'domain_neglect'
  | 'missed_streak'
  | 'parent_request'
  | 'cycle_review'

export type AdaptationProposalKind =
  | 'swap_block'
  | 'adjust_difficulty'
  | 'change_approach'
  | 'retheme_week'
  | 'add_block'
  | 'pause_skill'
  | 'update_skill_level'

export interface Adaptation {
  _id: AdaptationId
  childId: ChildId
  trigger: AdaptationTrigger
  detectedAt: Timestamp
  evidence: {
    observationIds: ObservationId[]
    skillIds: SkillId[]
    summary: string
  }
  /** Plain language. The parent must understand WHY. */
  rationale: string
  proposal: {
    kind: AdaptationProposalKind
    before?: unknown
    after?: unknown
    affectedIds: string[]
  }
  status: 'proposed' | 'accepted' | 'rejected' | 'auto_applied' | 'expired'
  decidedAt?: Timestamp
  parentNote?: string
  /** Dedupe key so a rejected proposal doesn't nag. */
  dedupeKey: string
  updatedAt: Timestamp
  deleted?: boolean
}

// ─────────────────────────────────────────────────────────────
// Chat
// ─────────────────────────────────────────────────────────────

export interface ToolCall {
  id: string
  name: string
  input: Record<string, unknown>
  status: 'running' | 'complete' | 'error'
  output?: unknown
  error?: string
}

export type MessageAttachment =
  | { kind: 'plan_diff'; before: unknown; after: unknown; adaptationId?: AdaptationId }
  | { kind: 'interactive'; artifactId: ArtifactId; spec: InteractiveSpec }
  | { kind: 'chart'; chartType: string; data: unknown }
  /** `at`: where in the text the agent showed it (see splitAtCards). */
  | { kind: 'artifact'; artifactId: ArtifactId; at?: number }
  /** An activity card from the plan, shown inside a message. */
  | { kind: 'block'; blockId: string; at?: number }
  | { kind: 'evidence'; observationIds: ObservationId[]; skillId?: SkillId }

export interface Message {
  _id: MessageId
  conversationId: ConversationId
  childId: ChildId
  role: 'user' | 'assistant'
  content: string
  toolCalls?: ToolCall[]
  attachments?: MessageAttachment[]
  imageArtifactIds?: ArtifactId[]
  streaming?: boolean
  createdAt: Timestamp
  client_id?: string
  updatedAt: Timestamp
  deleted?: boolean
}

export interface Conversation {
  _id: ConversationId
  childId: ChildId
  title: string
  lastMessageAt: Timestamp
  createdAt: Timestamp
  updatedAt: Timestamp
  deleted?: boolean
}

// ─────────────────────────────────────────────────────────────
// Derived / view types
// ─────────────────────────────────────────────────────────────

export interface DomainSummary {
  domainKey: DomainKey
  /** Confidence-weighted aggregate of member skills. */
  level: number
  confidence: number
  trend: Trend
  skillCount: number
  observedSkillCount: number
  lastObservedAt?: Timestamp
}

export interface AgeInMonths {
  months: number
  years: number
  remainderMonths: number
  /** e.g. "2y 9m" */
  label: string
}
